import {
  trackPwaAppInstalled,
  trackPwaInstallPromptAccepted,
  trackPwaInstallPromptDismissed,
} from '../analytics/pwaInstallAnalytics';
import { markPwaInstalledInStorage } from './pwaInstallStorage';

type BeforeInstallPromptEventLike = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let listenersInstalled = false;
let deferred: BeforeInstallPromptEventLike | null = null;
const subscribers = new Set<() => void>();

function notify() {
  subscribers.forEach((cb) => {
    try {
      cb();
    } catch {
      /* ignore */
    }
  });
}

export function ensurePwaInstallEventListeners(): void {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;

  const onBeforeInstallPrompt = (e: Event) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEventLike;
    notify();
  };

  const onAppInstalled = () => {
    deferred = null;
    markPwaInstalledInStorage();
    trackPwaAppInstalled();
    notify();
  };

  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);
}

export function subscribePwaInstallPrompt(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

export function getDeferredInstallPrompt(): BeforeInstallPromptEventLike | null {
  return deferred;
}

export async function runDeferredInstallPrompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const ev = deferred;
  if (!ev?.prompt) return 'unavailable';
  try {
    await ev.prompt();
    const choice = await ev.userChoice;
    deferred = null;
    notify();
    const accepted = choice?.outcome === 'accepted';
    if (accepted) {
      trackPwaInstallPromptAccepted();
    } else {
      trackPwaInstallPromptDismissed();
    }
    return accepted ? 'accepted' : 'dismissed';
  } catch {
    deferred = null;
    notify();
    return 'unavailable';
  }
}
